/** Details card + shared modal — port of openModal/closeModal incl. the
 * Wikipedia REST photo fetch (silently skipped on failure). */
import { esc, safeUrl, t12 } from "@trip/shared/engine/time";
import { store } from "../store.js";
import { priceLabel } from "./price.js";
import { runSheetCleanups } from "./sheet.js";

export function openModal(id: string) {
  const a = store.def.act[id];
  if (!a) return;
  // Curated activities may carry hand-written HTML (transport notes with
  // operator links); anything added in-app is plain text. The origin map is
  // the line between "trusted extraction output" and "someone typed this".
  const curated = (store.def.actOrigin?.[id] ?? "extracted") === "extracted";
  const g: [string, string][] = [];
  if (a.addr) g.push(["📍", esc(a.addr)]);
  // a rating when the price yields one, the price text when it doesn't — a
  // third of the curated prices ("Free", "€€", "Included with the walls
  // ticket") carry no number, and dropping the row made them disappear
  const price = priceLabel(a.price, a.cat);
  if (price) g.push(["💵", "<b>" + esc(price) + "</b>"]);
  if (a.hours) g.push(["🕒", esc(a.hours)]);
  const web = safeUrl(a.web);
  if (web)
    g.push(["🔗", `<a href="${web}" target="_blank" rel="noopener">${esc(web.replace(/^https?:\/\/(www\.)?/, "").split("/")[0])}</a>`]);
  if (a.tel) g.push(["📞", esc(a.tel)]);
  const modalEl = document.getElementById("modal")!;
  modalEl.style.maxWidth = "";
  modalEl.innerHTML = `
    <div class="m-head"><div class="m-cat">${esc(a.cat)}</div><h3>${esc(a.name)}</h3>
      <button class="m-close" data-action="close" aria-label="close">✕</button></div>
    <div class="m-photowrap" id="mphoto" data-act="${id}" style="display:none"><img class="m-photo" alt="" loading="lazy"><span class="m-photocap"></span></div>
    <div class="m-body">
      <div class="m-grid">${g.map((r) => `<div class="gi">${r[0]}</div><div class="gv">${r[1]}</div>`).join("")}</div>
      ${a.transport ? `<div class="m-book m-transport"><b>🚕 Getting there:</b> ${curated ? t12(a.transport) : esc(a.transport)}</div>` : ""}
      ${a.book ? `<div class="m-book"><b>Booking:</b> ${esc(a.book)}</div>` : ""}
      ${a.notes ? `<div class="m-notes">${esc(a.notes)}</div>` : ""}
      ${
        a.src && a.src.length
          ? `<div class="m-src">Sources: ${a.src
              .map((u) => {
                const s = safeUrl(u);
                return s ? `<a href="${s}" target="_blank" rel="noopener">${esc(s.replace(/^https?:\/\/(www\.)?/, "").split("/")[0])}</a>` : "";
              })
              .filter(Boolean)
              .join(" · ")}</div>`
          : ""
      }
    </div>`;
  document.getElementById("modalbg")!.classList.add("open");
  if (a.wiki) {
    fetch("https://en.wikipedia.org/api/rest_v1/page/summary/" + encodeURIComponent(a.wiki.replace(/ /g, "_")))
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        const w = document.getElementById("mphoto");
        if (!j || !w || w.dataset.act !== id) return;
        const u = (j.originalimage && j.originalimage.source) || (j.thumbnail && j.thumbnail.source);
        if (!u) return;
        (w.querySelector("img") as HTMLImageElement).src = u;
        // third-party URL: through safeUrl like every other href, even though
        // the source is Wikipedia's API
        const page = safeUrl(j.content_urls && j.content_urls.desktop && j.content_urls.desktop.page);
        w.querySelector(".m-photocap")!.innerHTML = page
          ? `Photo · <a href="${page}" target="_blank" rel="noopener">Wikipedia</a>`
          : "Photo · Wikipedia";
        (w as HTMLElement).style.display = "block";
      })
      .catch(() => {});
  }
}

let _dayMapDispose: (() => void) | null = null;
export function setDayMapDispose(fn: (() => void) | null) {
  _dayMapDispose = fn;
}

export function closeModal() {
  // the shell is created on demand by sheet.ts on pages that don't render it,
  // so it may legitimately not exist yet — closing nothing is a no-op
  document.getElementById("modalbg")?.classList.remove("open");
  if (_dayMapDispose) {
    _dayMapDispose();
    _dayMapDispose = null;
  }
  // full-screen sheets share this modal; every dismissal path lands here, so
  // their registered disposers (maps, observers) run on all of them
  runSheetCleanups();
}
